import React, { useMemo, useRef } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';
import CardActions from './CardActions';

const ACCENT = '#d9903d';

const capitalize = value => value.charAt(0).toUpperCase() + value.slice(1);

function MetaStat({ icon, label, value, colors }) {
  return (
    <View style={styles.metaStat}>
      <Icon name={icon} size={14} color={colors.accent} />
      <Text style={[styles.metaLabel, { color: colors.secondary }]}>{label}</Text>
      <Text style={[styles.metaValue, { color: colors.primary }]} numberOfLines={1}>{value}</Text>
    </View>
  );
}

MetaStat.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  colors: PropTypes.object.isRequired,
};

function SectionHeader({ icon, title, colors }) {
  return (
    <View style={styles.sectionHeader}>
      <Icon name={icon} size={14} color={colors.accent} style={styles.sectionHeaderIcon} />
      <Text style={[styles.sectionTitle, { color: colors.primary }]}>{title}</Text>
    </View>
  );
}

SectionHeader.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  colors: PropTypes.object.isRequired,
};

function RecipeCard({ spec, colors: themeColors, conversationId, messageId }) {
  const captureTargetRef = useRef(null);

  const colors = useMemo(() => ({
    primary: themeColors.text_color_primary || '#e6e6e6',
    secondary: themeColors.text_color_secondary || '#a6a6a6',
    border: themeColors.border_color_input || '#2a2a2a',
    background: themeColors.solid_color_secondary || '#1c1c1c',
    band: themeColors.solid_color_primary || '#141414',
    accent: ACCENT,
  }), [themeColors]);

  const meta = useMemo(() => [
    { icon: 'clock', label: 'Prep', value: spec.prepTime },
    { icon: 'fire', label: 'Cook', value: spec.cookTime },
    { icon: 'stopwatch', label: 'Total', value: spec.totalTime },
    { icon: 'users', label: 'Serves', value: spec.servings },
    { icon: 'star', label: 'Level', value: spec.difficulty ? capitalize(spec.difficulty) : '' },
  ].filter(item => item.value), [spec]);

  const tagLine = useMemo(() => (
    spec.cuisine ? [spec.cuisine, ...spec.tags] : spec.tags
  ), [spec]);

  return (
    <View
      accessibilityRole="summary"
      accessibilityLabel={`Recipe: ${spec.title}. ${spec.ingredients.length} ingredients, ${spec.steps.length} steps.`}
      style={[styles.container, { backgroundColor: colors.background, borderColor: colors.border }]}
    >
      <View
        ref={captureTargetRef}
        collapsable={false}
        dataSet={{ cardCapture: '' }}
        style={[styles.captureTarget, { backgroundColor: colors.background }]}
      >
        <View style={styles.header}>
          <View style={[styles.headerIcon, { backgroundColor: `${ACCENT}26`, borderColor: `${ACCENT}55` }]}>
            <Icon name="utensils" size={17} color={ACCENT} />
          </View>
          <View style={styles.headerText}>
            <Text style={[styles.title, { color: colors.primary }]}>{spec.title}</Text>
            {!!spec.description && (
              <Text style={[styles.description, { color: colors.secondary }]}>{spec.description}</Text>
            )}
          </View>
        </View>

        {tagLine.length > 0 && (
          <View style={styles.tags}>
            {tagLine.map(tag => (
              <View key={tag} style={[styles.tag, { borderColor: colors.border, backgroundColor: colors.band }]}>
                <Text style={[styles.tagText, { color: colors.secondary }]}>{tag}</Text>
              </View>
            ))}
          </View>
        )}

        {meta.length > 0 && (
          <View style={[styles.metaBand, { backgroundColor: colors.band, borderColor: colors.border }]}>
            {meta.map(item => (
              <MetaStat key={item.label} icon={item.icon} label={item.label} value={item.value} colors={colors} />
            ))}
          </View>
        )}

        <SectionHeader icon="list-check" title="Ingredients" colors={colors} />
        {spec.ingredientGroups.map((group, groupIndex) => (
          <View key={`group-${groupIndex}`} style={styles.ingredientGroup}>
            {!!group.section && (
              <Text style={[styles.ingredientSection, { color: colors.secondary }]}>{group.section}</Text>
            )}
            <View style={styles.ingredients}>
              {group.items.map((ingredient, index) => (
                <View key={`ingredient-${groupIndex}-${index}`} style={styles.ingredientItem}>
                  <Icon name="circle-check" size={13} color={ACCENT} style={styles.ingredientBullet} />
                  <Text style={[styles.ingredientText, { color: colors.primary }]}>{ingredient}</Text>
                </View>
              ))}
            </View>
          </View>
        ))}

        <SectionHeader icon="list-ol" title="Instructions" colors={colors} />
        <View style={styles.steps}>
          {spec.steps.map((step, index) => (
            <View key={`step-${index}`} style={styles.stepItem}>
              <View style={[styles.stepNumber, { backgroundColor: `${ACCENT}26`, borderColor: `${ACCENT}55` }]}>
                <Text style={[styles.stepNumberText, { color: ACCENT }]}>{index + 1}</Text>
              </View>
              <Text style={[styles.stepText, { color: colors.primary }]}>{step}</Text>
            </View>
          ))}
        </View>

        {spec.tips.length > 0 && (
          <View style={[styles.tipsBox, { borderLeftColor: ACCENT, backgroundColor: colors.band }]}>
            <View style={styles.tipsHeader}>
              <Icon name="lightbulb" size={13} color={ACCENT} style={styles.tipsHeaderIcon} />
              <Text style={[styles.tipsTitle, { color: ACCENT }]}>Chef&apos;s Tips</Text>
            </View>
            {spec.tips.map((tip, index) => (
              <Text key={`tip-${index}`} style={[styles.tipText, { color: colors.secondary }]}>{tip}</Text>
            ))}
          </View>
        )}
      </View>

      <CardActions
        conversationId={conversationId}
        messageId={messageId}
        captureTargetRef={captureTargetRef}
        exportTitle={spec.title}
        accentColor={ACCENT}
        borderColor={colors.border}
        bandColor={colors.band}
        textColor={colors.secondary}
      />
    </View>
  );
}

RecipeCard.propTypes = {
  spec: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  conversationId: PropTypes.string,
  messageId: PropTypes.string,
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
    flexShrink: 1,
    borderWidth: 1,
    borderRadius: 14,
    marginVertical: 8,
    position: 'relative',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 18px rgba(0,0,0,0.22)',
    }),
  },
  captureTarget: {
    borderRadius: 14,
    padding: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  headerIcon: {
    width: 38,
    height: 38,
    borderRadius: 11,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    marginRight: 12,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 19,
    fontWeight: '700',
    lineHeight: 25,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginTop: 4,
  },
  tags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 12,
  },
  tag: {
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginRight: 6,
    marginBottom: 6,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '500',
  },
  metaBand: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderWidth: 1,
    borderRadius: 11,
    paddingVertical: 10,
    paddingHorizontal: 6,
    marginTop: 6,
  },
  metaStat: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 72,
    minWidth: 0,
    alignItems: 'center',
    paddingVertical: 2,
  },
  metaLabel: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: 3,
  },
  metaValue: {
    fontSize: 14,
    fontWeight: '700',
    marginTop: 3,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
    marginBottom: 9,
  },
  sectionHeaderIcon: {
    marginRight: 7,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.9,
  },
  ingredientGroup: {
    marginBottom: 4,
  },
  ingredientSection: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.4,
    marginTop: 6,
    marginBottom: 7,
  },
  ingredients: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  ingredientItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 230,
    paddingRight: 14,
    marginBottom: 8,
  },
  ingredientBullet: {
    marginTop: 3,
    marginRight: 8,
  },
  ingredientText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  stepNumber: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    marginTop: 1,
  },
  stepNumberText: {
    fontSize: 13,
    fontWeight: '700',
  },
  stepText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 21,
  },
  tipsBox: {
    marginTop: 10,
    borderLeftWidth: 3,
    borderRadius: 8,
    paddingHorizontal: 13,
    paddingVertical: 11,
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  tipsHeaderIcon: {
    marginRight: 7,
  },
  tipsTitle: {
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  tipText: {
    fontSize: 13,
    lineHeight: 19,
    marginTop: 4,
  },
});

export default React.memo(RecipeCard);
